import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  FileText, 
  Home,
  Settings, 
  LogOut, 
  Plus,
  ChevronRight,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Menu,
  X,
  Building,
  Shield,
  Handshake,
  Receipt,
  Download,
  Mail,
  Loader2,
  CreditCard,
  Trash2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const orderStatusConfig = {
  draft: { label: "Brouillon", color: "bg-gray-100 text-gray-800", icon: FileText },
  checkout_created: { label: "En attente de paiement", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  paid_pending_property_data: { label: "Payé - En attente des logements", color: "bg-blue-100 text-blue-800", icon: AlertCircle },
  partially_completed: { label: "Partiellement complété", color: "bg-orange-100 text-orange-800", icon: AlertCircle },
  ready_to_activate: { label: "Prêt à activer", color: "bg-green-100 text-green-800", icon: CheckCircle },
  completed: { label: "Complété", color: "bg-green-100 text-green-800", icon: CheckCircle },
  cancelled: { label: "Annulé", color: "bg-red-100 text-red-800", icon: XCircle },
  refunded: { label: "Remboursé", color: "bg-gray-100 text-gray-800", icon: XCircle } };

const propertyStatusConfig = {
  draft: { label: "Brouillon", color: "bg-gray-100 text-gray-800" },
  ready_to_submit: { label: "Prêt à activer", color: "bg-yellow-100 text-yellow-800" },
  submitted: { label: "En cours d'activation", color: "bg-blue-100 text-blue-800" },
  active: { label: "Actif", color: "bg-green-100 text-green-800" },
  review_required: { label: "Révision requise", color: "bg-orange-100 text-orange-800" },
  blocked: { label: "Bloqué", color: "bg-red-100 text-red-800" },
  suspended: { label: "Suspendu", color: "bg-orange-100 text-orange-700" } };

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [properties, setProperties] = useState([]);
  const [progression, setProgression] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  const handleResendVerification = async () => {
    setResendingVerification(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/resend-verification`,
        {},
        { withCredentials: true }
      );
      setVerificationSent(true);
      toast.success("Email de vérification envoyé. Consultez votre boîte de réception.");
    } catch (error) {
      const status = error.response?.status;
      if (status === 429) {
        toast.error("Trop de demandes. Réessayez dans une heure.");
      } else {
        toast.error(
          error.response?.data?.detail || "Impossible d'envoyer l'email de vérification."
        );
      }
    } finally {
      setResendingVerification(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/dashboard/`, {
        withCredentials: true });
      setOrders(data.orders || []);
      setProperties(data.properties || []);
      setProgression(data.progression || {});
      setInvoices(data.invoices || []);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const handleResumePayment = async (orderId, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const { data } = await axios.post(
        `${API_URL}/api/payments/checkout/create`,
        { order_id: orderId, origin_url: window.location.origin },
        { withCredentials: true }
      );
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Lien de paiement indisponible");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la reprise du paiement");
    }
  };

  const handleCancelOrder = async (orderId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Annuler cette commande ? Cette action est irréversible.")) return;
    try {
      await axios.post(
        `${API_URL}/api/orders/${orderId}/cancel`,
        {},
        { withCredentials: true }
      );
      toast.success("Commande annulée");
      fetchDashboardData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'annulation");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const paidOrders = orders.filter(o => [
    "paid_pending_property_data",
    "partially_completed",
    "ready_to_activate",
    "completed"
  ].includes(o.status));

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric" });
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR" }).format(amount);
  };

  const progressPercent = progression.total_purchased > 0
    ? Math.min(100, Math.round((progression.completed_properties / progression.total_purchased) * 100))
    : 0;

  return (
    <div className="min-h-screen bg-slate-50 font-['DM_Sans']">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-slate-100">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-hostprotect.png" alt="HostProtect" className="h-9 w-auto" />
            </Link>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-slate-400 hover:text-slate-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2">
            {user?.role === "admin" && (
              <Link
                to="/admin"
                className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg"
                data-testid="nav-admin"
              >
                <Shield className="w-5 h-5" />
                Administration
              </Link>
            )}
            <Link
              to="/dashboard"
              className="flex items-center gap-3 px-4 py-3 text-[#19294C] bg-slate-100 rounded-lg font-medium"
              data-testid="nav-dashboard"
            >
              <LayoutDashboard className="w-5 h-5" />
              Tableau de bord
            </Link>
            <Link
              to="/logements"
              className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg"
              data-testid="nav-properties"
            >
              <Home className="w-5 h-5" />
              Mes logements
            </Link>
            <button
              className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg w-full text-left"
              data-testid="nav-orders"
              onClick={() => {
                document.getElementById("orders-section")?.scrollIntoView({behavior: "smooth"});
                setSidebarOpen(false);
              }}
            >
              <FileText className="w-5 h-5" />
              Mes commandes
            </button>
            <button
              className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg w-full text-left"
              data-testid="nav-invoices"
              onClick={() => {
                document.getElementById("invoices-section")?.scrollIntoView({behavior: "smooth"});
                setSidebarOpen(false);
              }}
            >
              <Receipt className="w-5 h-5" />
              Mes factures
            </button>
            <button
              className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg w-full text-left"
              data-testid="nav-settings"
              onClick={() => {
                document.getElementById("settings-section")?.scrollIntoView({behavior: "smooth"});
                setSidebarOpen(false);
              }}
            >
              <Settings className="w-5 h-5" />
              Paramètres
            </button>
            <Link
              to="/partenaire"
              className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg"
              data-testid="nav-affiliate"
            >
              <Handshake className="w-5 h-5" />
              Programme partenaire
            </Link>
          </nav>

          {/* User Info & Logout */}
          <div className="p-4 border-t border-slate-100">
            <div className="px-4 py-3 mb-2">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg w-full text-left"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Déconnexion
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-slate-600 hover:text-slate-900"
            >
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-semibold text-[#19294C] font-['Outfit']">
              Tableau de bord
            </h1>
            <Link to="/checkout">
              <Button 
                className="bg-[#00B8E4] hover:bg-[#00a5ce] text-white"
                data-testid="new-subscription-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Nouvelle souscription</span>
              </Button>
            </Link>
          </div>
        </header>

        {/* Dashboard Content */}
        <main className="p-4 sm:p-6 lg:p-8" data-testid="dashboard-content">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00B8E4]"></div>
            </div>
          ) : (
            <>
              {/* Email verification banner — only for users who explicitly have email_verified=false */}
              {user?.email_verified === false && (
                <div
                  className="bg-amber-50 border border-amber-200 rounded-xl p-4 sm:p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4"
                  data-testid="email-verification-banner"
                >
                  <div className="flex items-start gap-3 flex-1">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-amber-900 font-['Outfit']">
                        Vérifiez votre adresse email
                      </h3>
                      <p className="text-sm text-amber-800 mt-1">
                        Un email de vérification a été envoyé à{" "}
                        <strong>{user?.email}</strong>. Vous devez confirmer votre
                        adresse pour pouvoir souscrire à une protection.
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={handleResendVerification}
                    disabled={resendingVerification || verificationSent}
                    className="bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap"
                    data-testid="resend-verification-btn"
                  >
                    {resendingVerification ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Envoi…
                      </>
                    ) : verificationSent ? (
                      "Email renvoyé ✓"
                    ) : (
                      "Renvoyer l'email"
                    )}
                  </Button>
                </div>
              )}

              {/* Welcome Card */}
              <div className="bg-gradient-to-r from-[#19294C] to-blue-600 rounded-2xl p-6 sm:p-8 text-white mb-8">
                <h2 className="text-2xl sm:text-3xl font-bold font-['Outfit']">
                  Bonjour, {user?.name?.split(' ')[0]} !
                </h2>
                <p className="mt-2 text-blue-100">
                  Bienvenue sur votre espace HostProtect.
                </p>
              </div>

              {/* Progression Card */}
              {progression.total_purchased > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-8" data-testid="progression-card">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-[#19294C] font-['Outfit']">
                      Progression de vos protections
                    </h3>
                    <span className="text-2xl font-bold text-[#00B8E4]">{progressPercent}%</span>
                  </div>
                  <Progress value={progressPercent} className="h-3 mb-4" />
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-2xl font-bold text-[#19294C]">{progression.total_purchased}</p>
                      <p className="text-xs text-slate-500">Protections achetées</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-2xl font-bold text-[#19294C]">{progression.total_properties}</p>
                      <p className="text-xs text-slate-500">Logements créés</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-2xl font-bold text-[#00B8E4]">{progression.completed_properties}</p>
                      <p className="text-xs text-slate-500">Logements actifs</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-2xl font-bold text-orange-500">{progression.remaining}</p>
                      <p className="text-xs text-slate-500">Restants à créer</p>
                    </div>
                  </div>
                  
                  {progression.remaining > 0 && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <strong>Prochaine étape :</strong> Complétez vos fiches logement pour activer vos protections.
                      </p>
                      <Link to="/logements">
                        <Button className="mt-3 bg-[#00B8E4] hover:bg-[#00a5ce] text-white" data-testid="go-to-properties-btn">
                          <Home className="w-4 h-4 mr-2" />
                          Gérer mes logements
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              )}

              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-[#00B8E4]" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Logements actifs</p>
                      <p className="text-2xl font-bold text-slate-900">{progression.completed_properties || 0}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                      <Clock className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">En attente</p>
                      <p className="text-2xl font-bold text-slate-900">{progression.ready_properties || 0}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Building className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Total logements</p>
                      <p className="text-2xl font-bold text-slate-900">{progression.total_properties || 0}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Properties */}
              {properties.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-8">
                  <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-[#19294C] font-['Outfit']">
                      Mes logements récents
                    </h3>
                    <Link to="/logements" className="text-[#00B8E4] text-sm hover:underline">
                      Voir tous
                    </Link>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {properties.slice(0, 3).map((property) => {
                      const status = propertyStatusConfig[property.status] || propertyStatusConfig.draft;
                      return (
                        <Link
                          key={property.id}
                          to={`/logements/${property.id}/editer`}
                          className="flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors"
                          data-testid={`property-row-${property.id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                              <Home className="w-5 h-5 text-slate-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{property.name}</p>
                              <p className="text-sm text-slate-500">
                                {property.risk_address?.city}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${status.color}`}>
                              {status.label}
                            </span>
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Orders Section */}
              <div id="orders-section" className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-8 scroll-mt-20">
                <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-[#19294C] font-['Outfit']">
                    Mes commandes
                  </h3>
                </div>
                {orders.length === 0 ? (
                  <div className="p-12 text-center">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 mb-4">Aucune commande pour le moment</p>
                    <Link to="/checkout">
                      <Button className="bg-[#00B8E4] hover:bg-[#00a5ce] text-white">
                        Souscrire maintenant
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {orders.map((order) => {
                      const status = orderStatusConfig[order.status] || orderStatusConfig.draft;
                      const StatusIcon = status.icon;
                      return (
                        <Link
                          key={order.id}
                          to={`/commande/${order.id}`}
                          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-6 py-4 hover:bg-slate-50 transition-colors"
                          data-testid={`order-row-${order.id}`}
                        >
                          {/* Top: icon + commande info + chevron (chevron only on mobile) */}
                          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${status.color.split(' ')[0]}`}>
                              <StatusIcon className={`w-5 h-5 ${status.color.split(' ')[1]}`} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-slate-900 truncate">
                                Commande #{order.id.slice(0, 8)}
                              </p>
                              <p className="text-sm text-slate-500 truncate">
                                {formatDate(order.created_at)} • {order.quantity} logement{order.quantity > 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>
                          {/* Bottom on mobile / right on desktop: status + price + actions */}
                          <div className="flex flex-wrap items-center justify-between sm:justify-end gap-2 sm:gap-4 pl-13 sm:pl-0">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${status.color}`}>
                              {status.label}
                            </span>
                            <span className="font-semibold text-slate-900 whitespace-nowrap">
                              {formatPrice(order.total_amount)}
                            </span>
                            {(order.status === "checkout_created" || order.status === "draft") && (
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={(e) => handleResumePayment(order.id, e)}
                                  className="p-2 rounded-md text-[#00B8E4] hover:bg-[#00B8E4]/10 transition-colors"
                                  title="Reprendre le paiement"
                                  aria-label="Reprendre le paiement"
                                  data-testid={`resume-payment-${order.id}`}
                                >
                                  <CreditCard className="w-5 h-5 sm:w-4 sm:h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => handleCancelOrder(order.id, e)}
                                  className="p-2 rounded-md text-red-600 hover:bg-red-50 transition-colors"
                                  title="Annuler la commande"
                                  aria-label="Annuler la commande"
                                  data-testid={`cancel-order-${order.id}`}
                                >
                                  <Trash2 className="w-5 h-5 sm:w-4 sm:h-4" />
                                </button>
                              </div>
                            )}
                            <ChevronRight className="hidden sm:block w-5 h-5 text-slate-400" />
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Warning Message */}
              {progression.total_purchased > 0 && progression.completed_properties < progression.total_purchased && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                  <div className="flex gap-4">
                    <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-amber-800">Information importante</p>
                      <p className="text-amber-700 mt-1">
                        La protection prendra effet uniquement après validation complète des informations du logement et activation du contrat.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Invoices Section */}
              <div id="invoices-section" className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="invoices-section">
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                  <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <Receipt className="w-5 h-5 text-[#00B8E4]" />
                    Mes factures
                  </h2>
                </div>
                {invoices.length === 0 ? (
                  <div className="px-6 py-8 text-center">
                    <Receipt className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Aucune facture pour le moment</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {invoices.map((inv) => (
                      <div key={inv.id} className="flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors" data-testid={`invoice-row-${inv.id}`}>
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${inv.status === "paid" ? "bg-green-100" : inv.status === "refunded" ? "bg-red-100" : "bg-gray-100"}`}>
                            <Receipt className={`w-5 h-5 ${inv.status === "paid" ? "text-green-600" : inv.status === "refunded" ? "text-red-600" : "text-gray-600"}`} />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{inv.description || "Facture HostProtect"}</p>
                            <p className="text-sm text-slate-500">{inv.paid_at ? formatDate(inv.paid_at) : formatDate(inv.created_at)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${inv.status === "paid" ? "bg-green-100 text-green-700" : inv.status === "refunded" ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-700"}`}>
                            {inv.status === "paid" ? "Payée" : inv.status === "refunded" ? "Remboursée" : inv.status}
                          </span>
                          <span className="font-semibold text-slate-900">{formatPrice(inv.amount)}</span>
                          {inv.invoice_pdf && (
                            <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer" className="p-2 text-[#00B8E4] hover:bg-[#00B8E4]/10 rounded-lg transition-colors" data-testid={`invoice-pdf-${inv.id}`}>
                              <Download className="w-4 h-4" />
                            </a>
                          )}
                          {inv.invoice_url && !inv.invoice_pdf && (
                            <a href={inv.invoice_url} target="_blank" rel="noopener noreferrer" className="p-2 text-[#00B8E4] hover:bg-[#00B8E4]/10 rounded-lg transition-colors">
                              <ChevronRight className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Settings Section */}
              <div id="settings-section" className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mt-8 scroll-mt-20" data-testid="settings-section">
                <div className="px-6 py-4 border-b border-slate-100">
                  <h3 className="text-lg font-semibold text-[#19294C] font-['Outfit']">
                    Paramètres du compte
                  </h3>
                </div>
                <div className="p-6 space-y-6">
                  {/* Account info */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Nom</p>
                      <p className="text-slate-900 font-medium">{user?.name || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Email</p>
                      <p className="text-slate-900 font-medium break-all">{user?.email || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Type de compte</p>
                      <p className="text-slate-900 font-medium capitalize">{user?.user_type || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Vérification email</p>
                      {user?.email_verified === false ? (
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800 font-medium">
                            <AlertCircle className="w-3 h-3" />
                            Non vérifié
                          </span>
                          <button
                            onClick={handleResendVerification}
                            disabled={resendingVerification || verificationSent}
                            className="text-sm text-[#00B8E4] hover:underline disabled:text-slate-400 disabled:no-underline"
                          >
                            {resendingVerification ? "Envoi…" : verificationSent ? "Email renvoyé ✓" : "Renvoyer le lien"}
                          </button>
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-100 text-green-800 font-medium">
                          <CheckCircle className="w-3 h-3" />
                          Vérifié
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="pt-4 border-t border-slate-100 flex flex-col sm:flex-row gap-3">
                    <Link
                      to="/mot-de-passe-oublie"
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 font-medium text-sm"
                      data-testid="settings-change-password-btn"
                    >
                      <Settings className="w-4 h-4" />
                      Changer mon mot de passe
                    </Link>
                    <Button
                      onClick={handleLogout}
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                      data-testid="settings-logout-btn"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Se déconnecter
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default DashboardPage;
